/**
 * 顾问陪跑 · 客户详情（草案 A-2：档案与背景 / 订单 / 问题清单与进度 / 操作记录）。
 *
 * 「档案与背景」= 客户（称呼、电话）+ 每本书各一份讲述人与背景（一单一书，爸妈各一份）。
 *
 * 订单只能「开」与「取消待付款」——没有「标记已付款」：订单只会因用户在小程序里
 * 真实支付成功而变成已付，付款后系统自动在付款人名下开书。
 *
 * 发送订单卡片（企微侧边栏）在 PR⑦，小程序订单页在 PR⑥；本页先给出订单号备查。
 * 「定稿与印刷」（PR⑤）：每本已付款开出的顾问书一块，见 fulfillment-panel.tsx。
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Descriptions, Divider, Empty, Form, Input, InputNumber, Modal, Popconfirm, Result, Space,
  Table, Tabs, Tag, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../../components/PermissionGate';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  cancelAdvisorOrder,
  createAdvisorOrder,
  getAdvisorCustomer,
  updateAdvisorCustomer,
  type AdvisorAssignmentLogDto,
  type AdvisorOrderDto,
} from '../../../api/advisor';
import {
  BookNarratorFields,
  CustomerFields,
  customerToFormValues,
  customerToPayload,
  type CustomerFormValues,
} from './profile-form';
import { BookProfilePanels } from './book-profile-panels';
import { narratorText, narratorsText } from './narrator';
import { useOrderAmountRange } from '../order-amount-range';
import { ORDER_VIEW_META } from './order-view';
import { ATTENTION_META, STAGE_META, bookTitle, lastNarratedText } from './stage';
import { WorkFulfillmentPanel } from './fulfillment-panel';
import { CardsTab } from './cards-tab';
import { ProgressTab } from './progress-tab';
import { AudioImportTab } from './audio-import-tab';

const { Text } = Typography;

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function yuan(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

const HOLD_LABELS: Record<string, string> = {
  on_hold: '佣金挂起·待判定',
  released: '已发给合伙人',
  waived: '不发',
  void: '已作废（退款）',
};

/** 各本书里最早的期望交付日期（YYYY-MM-DD 字符串可直接比大小）；都没填返回 '-' */
function earliestDeadline(orders: AdvisorOrderDto[]): string {
  const dates = orders
    .filter((o) => o.view !== 'cancelled')
    .map((o) => o.background?.deadline)
    .filter((d): d is string => typeof d === 'string' && d.length > 0)
    .sort();
  return dates[0] ?? '-';
}

type OrderFormValues = {
  amountYuan: number;
  productTitle?: string;
  expiresInDays: number;
  narratorName?: string;
  narratorRelation: string;
};

export default function AdvisorCustomerDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [profileForm] = Form.useForm<CustomerFormValues>();
  const [orderForm] = Form.useForm<OrderFormValues>();
  const [orderOpen, setOrderOpen] = useState(false);
  // 开单金额区间由超管在「顾问设置」里调；后端建单时才是真正的闸门
  const { minYuan: MIN_YUAN, maxYuan: MAX_YUAN } = useOrderAmountRange();

  const detailQuery = useQuery({
    queryKey: ['advisor-customer', id],
    queryFn: () => getAdvisorCustomer(id),
    retry: false,
  });
  const detail = detailQuery.data;

  useEffect(() => {
    if (detail) profileForm.setFieldsValue(customerToFormValues(detail));
  }, [detail, profileForm]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['advisor-customer', id] });
    queryClient.invalidateQueries({ queryKey: ['advisor-customers'] });
  };

  const saveMutation = useMutation({
    mutationFn: (values: CustomerFormValues) => updateAdvisorCustomer(id, customerToPayload(values)),
    onSuccess: () => {
      message.success('已保存');
      refresh();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '保存失败');
    },
  });

  const orderMutation = useMutation({
    mutationFn: (v: OrderFormValues) =>
      createAdvisorOrder(id, {
        amountFen: Math.round(v.amountYuan * 100),
        productTitle: v.productTitle?.trim() || undefined,
        expiresInDays: v.expiresInDays,
        // 一单一书：开单就说清这本书写的是谁，其余背景付款后在「档案与背景」里补
        narratorName: v.narratorName?.trim() || null,
        narratorRelation: v.narratorRelation.trim(),
      }),
    onSuccess: () => {
      message.success('已开单');
      setOrderOpen(false);
      orderForm.resetFields();
      refresh();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '开单失败');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: cancelAdvisorOrder,
    onSuccess: () => {
      message.success('订单已取消');
      refresh();
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '取消失败');
    },
  });

  if (detailQuery.isError) {
    const status = (detailQuery.error as any)?.response?.status;
    return (
      <Result
        status={status === 404 ? '404' : 'error'}
        title={status === 404 ? '客户不存在，或不在你名下' : '加载失败'}
        extra={<Button onClick={() => navigate('/advisor/customers')}>返回客户列表</Button>}
      />
    );
  }

  const orderColumns: ColumnsType<AdvisorOrderDto> = [
    {
      title: '订单',
      render: (_, o) => (
        <Space direction="vertical" size={0}>
          <Text>{o.productTitle}</Text>
          {narratorText(o.narratorName, o.narratorRelation) && (
            <Text type="secondary">讲述人：{narratorText(o.narratorName, o.narratorRelation)}</Text>
          )}
          <Text type="secondary" copyable={{ text: o.advisorOrderId }}>
            {o.advisorOrderId}
          </Text>
        </Space>
      ),
    },
    { title: '金额', dataIndex: 'amountFen', render: (v: number) => yuan(v) },
    {
      title: '状态',
      render: (_, o) => (
        <Space size={4} wrap>
          <Tag color={ORDER_VIEW_META[o.view].color}>{ORDER_VIEW_META[o.view].label}</Tag>
          {o.commissionHold && (
            <Tag color={o.commissionHold.status === 'on_hold' ? 'orange' : 'default'}>
              {HOLD_LABELS[o.commissionHold.status] ?? o.commissionHold.status}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: '时间',
      render: (_, o) =>
        o.view === 'paid' || o.view === 'refunded'
          ? `付款 ${formatTs(o.paidAt)}`
          : o.view === 'awaiting_payment'
            ? `${formatTs(o.expiresAt)} 前有效`
            : `创建 ${formatTs(o.createdAt)}`,
    },
    {
      title: '',
      render: (_, o) =>
        o.view === 'awaiting_payment' || o.view === 'expired' ? (
          <PermissionGate point={PERMISSIONS.ADVISOR_ORDER_CREATE}>
            <Popconfirm
              title="取消这张订单？"
              description="取消后用户打开订单页会看到「已取消」，需要时重新开单"
              onConfirm={() => cancelMutation.mutate(o.advisorOrderId)}
            >
              <Button size="small" danger>
                取消
              </Button>
            </Popconfirm>
          </PermissionGate>
        ) : null,
    },
  ];

  const logColumns: ColumnsType<AdvisorAssignmentLogDto> = [
    { title: '时间', dataIndex: 'createdAt', render: (v: number) => formatTs(v) },
    { title: '从', render: (_, l) => l.fromNickname ?? l.fromAdminId },
    { title: '转给', render: (_, l) => l.toNickname ?? l.toAdminId },
    { title: '操作人', render: (_, l) => l.actorNickname ?? '-' },
    { title: '原因', dataIndex: 'reason', render: (v: string | null) => v ?? '-' },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card loading={detailQuery.isLoading}>
        {detail && (
          <>
            <Descriptions
              title={
                <Space>
                  <span>{detail.displayName}</span>
                  {detail.linkedUser ? <Tag color="green">已开书</Tag> : <Tag>未付款</Tag>}
                </Space>
              }
              column={{ xs: 1, md: 3 }}
              extra={<Button onClick={() => navigate('/advisor/customers')}>返回列表</Button>}
            >
              <Descriptions.Item label="讲述人">{narratorsText(detail.narrators)}</Descriptions.Item>
              <Descriptions.Item label="各书阶段">
                {detail.books.length === 0 ? (
                  '-'
                ) : (
                  <Space direction="vertical" size={2}>
                    {detail.books.map((b) => (
                      <Space key={b.advisorOrderId} size={4} wrap>
                        <Tag color={STAGE_META[b.stage].color} style={{ marginInlineEnd: 0 }}>
                          {STAGE_META[b.stage].label}
                        </Tag>
                        <Text>{bookTitle(b) || '未填讲述人'}</Text>
                        {b.progress && (
                          <Text type="secondary">
                            已聊 {b.progress.discussed}/{b.progress.total}
                          </Text>
                        )}
                      </Space>
                    ))}
                  </Space>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="最近讲述">
                <Space size={4} wrap>
                  <Text type={detail.attention.includes('idle') ? 'danger' : undefined}>
                    {detail.books.some((b) => b.workId) ? lastNarratedText(detail.lastActiveAt) : '-'}
                  </Text>
                  {detail.attention.map((a) => (
                    <Tag key={a} color={ATTENTION_META[a].color} style={{ marginInlineEnd: 0 }}>
                      {ATTENTION_META[a].label}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="联系电话">{detail.contactPhone ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="归属销售">{detail.advisorNickname ?? detail.advisorAdminId}</Descriptions.Item>
              <Descriptions.Item label="最早期望交付">{earliestDeadline(detail.orders)}</Descriptions.Item>
              <Descriptions.Item label="建档时间">{formatTs(detail.createdAt)}</Descriptions.Item>
            </Descriptions>
            {detail.partnerOverlap && (
              <Alert
                type="warning"
                showIcon
                message={`合伙人交叉：该客户同时归属城市合伙人 ${detail.partnerOverlap.partnerId}`}
                description={<span>顾问订单的合伙人佣金已挂起，不会自动结算。去 <Link to="/advisor/overlaps">交叉用户</Link> 判定发不发。</span>}
              />
            )}
          </>
        )}
      </Card>

      {detail && (
        <Card>
          <Tabs
            items={[
              {
                key: 'profile',
                label: '档案与背景',
                children: (
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Form form={profileForm} layout="vertical" onFinish={(v) => saveMutation.mutate(v)}>
                      <Divider orientation="left" plain>客户</Divider>
                      <CustomerFields />
                      <PermissionGate point={PERMISSIONS.ADVISOR_CUSTOMER_WRITE}>
                        <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
                          保存客户档案
                        </Button>
                      </PermissionGate>
                    </Form>
                    <Divider orientation="left" plain>每本书的讲述人与背景</Divider>
                    <BookProfilePanels customerId={detail.customerId} orders={detail.orders} />
                  </Space>
                ),
              },
              {
                key: 'orders',
                label: `订单（${detail.orders.length}）`,
                children: (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Space>
                      <PermissionGate point={PERMISSIONS.ADVISOR_ORDER_CREATE}>
                        <Button type="primary" onClick={() => setOrderOpen(true)}>
                          开顾问订单
                        </Button>
                      </PermissionGate>
                      <Text type="secondary">
                        用户在小程序订单页付款成功后，系统自动在付款人名下开书；没有手动标记已付款。
                      </Text>
                    </Space>
                    <Table<AdvisorOrderDto>
                      rowKey="advisorOrderId"
                      scroll={{ x: 'max-content' }}
                      dataSource={detail.orders}
                      columns={orderColumns}
                      pagination={false}
                      locale={{ emptyText: <Empty description="还没有订单" /> }}
                    />
                  </Space>
                ),
              },
              {
                key: 'progress',
                label: '问题清单与进度',
                children: <ProgressTab detail={detail} />,
              },
              {
                key: 'recordings',
                label: '上传录音',
                children: <AudioImportTab detail={detail} />,
              },
              {
                key: 'fulfillment',
                label: '定稿与印刷',
                children: (() => {
                  const workIds = detail.orders
                    .filter((o) => o.view === 'paid' && o.workId)
                    .map((o) => o.workId as string);
                  return workIds.length === 0 ? (
                    <Empty description="客户付款后会自动开书，书开好后在这里定稿、下印刷单" />
                  ) : (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                      {workIds.map((workId) => (
                        <WorkFulfillmentPanel key={workId} workId={workId} />
                      ))}
                    </Space>
                  );
                })(),
              },
              {
                key: 'cards',
                label: '发送给客户',
                children: <CardsTab customerId={detail.customerId} />,
              },
              {
                key: 'logs',
                label: '转交记录',
                children: (
                  <Table<AdvisorAssignmentLogDto>
                    rowKey="logId"
                    scroll={{ x: 'max-content' }}
                    dataSource={detail.assignmentLogs}
                    columns={logColumns}
                    pagination={false}
                    locale={{ emptyText: '没有转交过' }}
                  />
                ),
              },
            ]}
          />
        </Card>
      )}

      <Modal
        title="开顾问订单"
        open={orderOpen}
        onCancel={() => setOrderOpen(false)}
        onOk={() => orderForm.submit()}
        okText="开单"
        confirmLoading={orderMutation.isPending}
        destroyOnHidden
      >
        <Form
          name="open_order"
          form={orderForm}
          layout="vertical"
          preserve={false}
          initialValues={{ expiresInDays: 7 }}
          onFinish={(v) => orderMutation.mutate(v)}
        >
          <BookNarratorFields />
          <Form.Item
            name="amountYuan"
            label="金额（元）"
            rules={[
              { required: true, message: '请填写金额' },
              {
                type: 'number',
                min: MIN_YUAN,
                max: MAX_YUAN,
                message: `金额须在 ¥${MIN_YUAN}–¥${MAX_YUAN} 之间`,
              },
            ]}
          >
            <InputNumber min={MIN_YUAN} max={MAX_YUAN} precision={2} style={{ width: '100%' }} prefix="¥" />
          </Form.Item>
          <Form.Item name="productTitle" label="商品标题" tooltip="显示在用户的订单页；不填用默认标题">
            <Input maxLength={128} placeholder="精装回忆录定制 · 顾问陪伴版" />
          </Form.Item>
          <Form.Item
            name="expiresInDays"
            label="有效期（天）"
            rules={[{ required: true }, { type: 'number', min: 1, max: 30, message: '1–30 天' }]}
          >
            <InputNumber min={1} max={30} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            message="一单一书：订单含 1 本精装实体书，用户付款时填写收货地址。给爸妈各做一本，就各开一单、各填各的讲述人；背景付款后在「档案与背景」里补。"
          />
        </Form>
      </Modal>
    </Space>
  );
}
